import React from 'react';
import { connect } from 'react-redux';
import { LocalizedMessage, LocalizedStandardDate } from 'intl/LocalizedComponents';
import classnames from 'classnames';
import _find from 'lodash/find';
import _get from 'lodash/get';
import _map from 'lodash/map';
import PropTypes from 'prop-types';
// HOCs
import withScroll from 'hocs/WithScroll';
// Core components
import Link from 'components/core/Link';
import TableRow from 'components/core/table/TableRow';
import TableCell from 'components/core/table/TableCell';
import Tooltip from 'components/core/Tooltip';
// Models
import mAsync from 'models/async';
import mDevice from 'models/device';
import mInstallationAsyncOperation from 'models/installationAsyncOperation';
// Custom
import { ASYNC_OP_STATUS_IGNORED, TABLE_ROW_HEIGHT } from 'constants/App';
import {
  listRowSpecsShape, installationAsyncOperationDataShape, installationAsyncOperationDeviceDataShape, deviceDataShape,
} from 'constants/PropTypesShapes';
import { showPath as showDevicePath } from 'devices/navigation';
import { HistoryIconButton } from 'components/buttons/IconButtons';
import AppIcon from 'components/icons/AppIcon';
import TruncatedTooltipText from 'components/misc/TruncatedTooltipText';
import withDeviceData from 'appAssets/hocs/WithDeviceData';

const AsyncOperationMethod = ({ installationAsyncOperation }) => {
  const { labelId } = mInstallationAsyncOperation.method(installationAsyncOperation) || {};

  if (labelId) {
    return (
      <TruncatedTooltipText>
        <LocalizedMessage id={labelId} />
      </TruncatedTooltipText>
    );
  }
  return null;
};

AsyncOperationMethod.propTypes = {
  installationAsyncOperation: installationAsyncOperationDataShape.isRequired,
};

const AsyncOperationResource = ({ installationAsyncOperation }) => {
  const resourceType = mInstallationAsyncOperation.resourceType(installationAsyncOperation);

  if (resourceType) {
    return (
      <TruncatedTooltipText>
        {installationAsyncOperation.resource_id}
        {' - '}
        <LocalizedMessage id={resourceType.labelId} />
      </TruncatedTooltipText>
    );
  }
  return null;
};

AsyncOperationResource.propTypes = {
  installationAsyncOperation: installationAsyncOperationDataShape.isRequired,
};

const AsyncOperationResult = ({ installationAsyncOperation }) => {
  const { icon: statusIcon, color: statusColor, labelId: statusLabelId } = mInstallationAsyncOperation.status(installationAsyncOperation);
  const { icon: rollbackStatusIcon, color: rollbackStatusColor, labelId: rollbackStatusLabelId } = mInstallationAsyncOperation.rollbackStatus(installationAsyncOperation);

  return (
    <div className="d-flex align-items-center">
      <Tooltip content={<LocalizedMessage id={statusLabelId} />}>
        <AppIcon id={statusIcon} color={statusColor} className="mr-1" />
      </Tooltip>
      <AppIcon defaultColor id="thus" className="mr-1" />
      <Tooltip content={<LocalizedMessage id={rollbackStatusLabelId} />}>
        <AppIcon id={rollbackStatusIcon} color={rollbackStatusColor} />
      </Tooltip>
    </div>
  );
};

AsyncOperationResult.propTypes = {
  installationAsyncOperation: installationAsyncOperationDataShape.isRequired,
};

const AsyncOperationDeviceStatus = withDeviceData('device.device_id')(({ device, deviceData }) => {
  // Real status including eventual ignored flag
  const deviceStatus = device.sync_error_ignored ? ASYNC_OP_STATUS_IGNORED : device.sync_status;
  const { color } = mAsync.statusIcon(deviceStatus);

  const tooltipContent = (
    <React.Fragment>
      <div>{deviceData && mDevice.installationDesc(deviceData)}</div>
      <div><LocalizedMessage id={mAsync.statusLabel(deviceStatus, device.sync_error_code)} /></div>
      <div className="box-divider-light m-0 my-2" />
      <div className="w-100 text-center">
        <LocalizedMessage id="tooltip.displayInNew" />
      </div>
    </React.Fragment>
  );

  return deviceData && deviceData.id ? (
    <Tooltip content={tooltipContent}>
      <Link path={{ path: showDevicePath(deviceData.id), targetBlank: true }}>
        <AppIcon id={mDevice.icon} color={color} className="mr-2" />
      </Link>
    </Tooltip>
  ) : null;
});

AsyncOperationDeviceStatus.propTypes = {
  device: installationAsyncOperationDeviceDataShape.isRequired,
  deviceData: deviceDataShape,
};

AsyncOperationDeviceStatus.defaultProps = {
  deviceData: null,
};

const AsyncOperationDevices = withScroll(({ installationAsyncOperationDevices }) => (
  <div className="d-flex h-100 align-items-center">
    {_map(mDevice.sortElements(installationAsyncOperationDevices, ['name']), (device) => (
      <AsyncOperationDeviceStatus
        key={device.device_id}
        device={device}
      />
    ))}
  </div>
));

AsyncOperationDevices.propTypes = {
  installationAsyncOperationDevices: PropTypes.arrayOf(installationAsyncOperationDeviceDataShape).isRequired,
};

const AsyncOperationRow = React.forwardRef(({
  installationDevices, installationAsyncOperation, rowSpecs,
  withResource, onDisplayAsyncOperationHistory,
}, ref) => {
  const { id: installationAsyncOperationId } = installationAsyncOperation;

  // Create a map of device by adding to AO device the name (for sort purpose)
  const installationAsyncOperationDevices = _map(installationAsyncOperation.devices, (device) => ({
    ...device,
    name: _get(_find(installationDevices, ['id', device.device_id]), 'name'),
  }));

  // Handlers
  const handleDisplayAsyncOperationHistory = () => {
    onDisplayAsyncOperationHistory(installationAsyncOperation);
  };

  const actions = [];
  actions.push(
    <HistoryIconButton
      key="btnHistory"
      onClick={handleDisplayAsyncOperationHistory}
      tooltipId="tooltip.resourceAsyncOperationsHistory"
    />,
  );

  return (
    <TableRow
      ref={ref}
      element={installationAsyncOperation}
      rowSpecs={rowSpecs}
      actions={actions}
    >
      <TableCell className={classnames('table-content-1', { 'table-first-details': rowSpecs.withDetails })}>
        <TruncatedTooltipText>
          {installationAsyncOperationId}
        </TruncatedTooltipText>
      </TableCell>
      <TableCell className="table-content-4 hidden-xs-down">
        <TruncatedTooltipText>
          <LocalizedStandardDate value={installationAsyncOperation.date} />
        </TruncatedTooltipText>
      </TableCell>
      <TableCell className="table-content-1 hidden-md-down">
        <TruncatedTooltipText>
          {installationAsyncOperation.author_id}
        </TruncatedTooltipText>
      </TableCell>
      <TableCell className="table-content-2 hidden-sm-down">
        <AsyncOperationMethod installationAsyncOperation={installationAsyncOperation} />
      </TableCell>
      {withResource && (
        <TableCell className="hidden-sm-down">
          <AsyncOperationResource installationAsyncOperation={installationAsyncOperation} />
        </TableCell>
      )}
      <TableCell className="table-content-2 table-content-center">
        <AsyncOperationResult installationAsyncOperation={installationAsyncOperation} />
      </TableCell>
      <TableCell className="hidden-md-down" noPadding>
        <div style={{ height: TABLE_ROW_HEIGHT }}>
          <AsyncOperationDevices installationAsyncOperationDevices={installationAsyncOperationDevices} />
        </div>
      </TableCell>
    </TableRow>
  );
});

AsyncOperationRow.displayName = 'AsyncOperationRow';

AsyncOperationRow.propTypes = {
  installationDevices: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ).isRequired,
  installationAsyncOperation: installationAsyncOperationDataShape.isRequired,
  rowSpecs: listRowSpecsShape.isRequired,
  withResource: PropTypes.bool,
  onDisplayAsyncOperationHistory: PropTypes.func,
};

AsyncOperationRow.defaultProps = {
  withResource: true,
  onDisplayAsyncOperationHistory: null,
};

const mapStateToProps = (state, ownProps) => ({
  installationDevices: ownProps.installationDevices || state.installation.current.data.devices,
});

module.exports = connect(
  mapStateToProps,
)(AsyncOperationRow);



// WEBPACK FOOTER //
// ./routes/app/routes/installations/assets/asyncOperations/AsyncOperationRow.js